// src/config/imageConfig.js
// ======================================================
// 🔧 CONFIGURACIÓN ULTRA PROFESIONAL DE IMÁGENES (NIVEL AIRBNB)
// ======================================================
// ✅ Cloudinary como proveedor principal (CDN global)
// ✅ Transformaciones CDN automáticas para máxima velocidad
// ✅ Calidad adaptativa por dispositivo (móvil/desktop)
// ✅ Formatos modernos (WebP/AVIF) automáticos
// ✅ Preparado para migrar a Firebase Storage
// ✅ Cache offline con IndexedDB
// ======================================================

// ======================================================
// 🌐 PROVEEDOR DE IMÁGENES
// ======================================================

export const IMAGE_PROVIDER = {
  CURRENT: 'cloudinary',
  FALLBACK: 'indexeddb',
  AVAILABLE: ['cloudinary', 'firebase', 'indexeddb']
};

// ======================================================
// ☁️ CONFIGURACIÓN CLOUDINARY (CDN PROFESIONAL)
// ======================================================

export const CLOUDINARY_CONFIG = {
  // Credenciales
  cloudName: import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'do8zpazwh',
  uploadPreset: import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || 'imoveis_pro_preset',
  folder: import.meta.env.VITE_CLOUDINARY_FOLDER || 'imoveis_pro',
  
  // Límites
  maxFileSize: 10 * 1024 * 1024, // 10MB
  maxImagesPerProperty: 10,
  allowedFormats: ['jpg', 'jpeg', 'png', 'webp', 'avif', 'heic', 'heif'],
  
  // ⚡ TRANSFORMACIONES CDN ULTRA RÁPIDAS
  transformations: {
    // Calidad automática según dispositivo
    quality: 'auto:best',
    // Formato automático (WebP/AVIF si soporta)
    fetchFormat: 'auto',
    // Dimensiones máximas
    width: 1600,
    height: 1600,
    crop: 'limit',
    // Densidad de píxeles automática
    dpr: 'auto',
    // Carga progresiva
    flags: 'progressive',
    // Compresión sin pérdida visible
    compression: 'auto'
  },
  
  // 🎨 TRANSFORMACIONES POR CONTEXTO
  // ⚡ Presets balance calidad/velocidad (Airbnb/Zillow style)
  presets: {
    thumbnail: 'w_160,h_120,c_fill,g_auto,q_auto:good,f_auto,dpr_auto',
    card: 'w_720,h_540,c_fill,g_auto,q_auto:good,f_auto,dpr_auto,fl_progressive',
    preview: 'w_1080,h_810,c_fill,g_auto,q_auto:good,f_auto,dpr_auto,fl_progressive',
    fullscreen: 'w_1600,h_1600,c_limit,q_auto:best,f_auto,dpr_auto,fl_progressive',
    original: 'q_auto:best,f_auto,dpr_auto,fl_progressive'
  },
  
  // URLs de API
  api: {
    upload: 'https://api.cloudinary.com/v1_1',
    fetch: 'https://res.cloudinary.com'
  },
  
  // Timeouts y reintentos
  timeout: 60000,        // 60 segundos
  maxRetries: 3,         // 3 reintentos
  retryDelay: 1000,      // 1 segundo entre reintentos
  
  // Subida paralela
  parallelUploads: 3,    // 3 imágenes simultáneas
  
  // Debug
  debug: import.meta.env.DEV || false
};

// ======================================================
// 🔥 CONFIGURACIÓN FUTURA: FIREBASE STORAGE
// ======================================================

export const FIREBASE_STORAGE_CONFIG = {
  bucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  folder: 'imoveis_pro',
  maxFileSize: 10 * 1024 * 1024,
  allowedFormats: ['jpg', 'jpeg', 'png', 'webp', 'avif']
};

// ======================================================
// 📦 CONFIGURACIÓN DE CACHE OFFLINE (INDEXEDDB)
// ======================================================

export const CACHE_CONFIG = {
  databaseName: 'imoveispro_shared_images',
  version: 2,
  maxCacheSize: 100 * 1024 * 1024,  // 100MB
  maxCacheItems: 500,               // 500 imágenes
  expirationDays: 30,               // 30 días
  cleanupInterval: 24 * 60 * 60 * 1000, // Diario
  // LRU Cache en memoria
  memoryCacheSize: 50               // 50 imágenes en RAM
};

// ======================================================
// 🎨 CONFIGURACIÓN DE THUMBNAILS (RESPONSIVE)
// ======================================================

export const THUMBNAIL_CONFIG = {
  // Tamaños optimizados para cada contexto
  sm: { width: 150, height: 150, quality: 'auto:good', format: 'auto' },
  md: { width: 400, height: 400, quality: 'auto:good', format: 'auto' },
  lg: { width: 800, height: 800, quality: 'auto:best', format: 'auto' },
  xl: { width: 1600, height: 1600, quality: 'auto:best', format: 'auto' },
  
  // Detectar automáticamente el mejor tamaño
  getOptimalSize: (containerWidth) => {
    if (containerWidth <= 150) return 'sm';
    if (containerWidth <= 400) return 'md';
    if (containerWidth <= 800) return 'lg';
    return 'xl';
  }
};

// ======================================================
// 📱 DETECCIÓN DE DISPOSITIVO Y RED
// ======================================================

export const getDeviceConfig = () => {
  const isMobile = typeof window !== 'undefined' && 
    (window.innerWidth < 768 || /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent));
  
  const isSlowConnection = typeof navigator !== 'undefined' && 
    (navigator.connection?.effectiveType === 'slow-2g' || 
     navigator.connection?.effectiveType === '2g' ||
     navigator.connection?.saveData === true);
  
  return {
    isMobile,
    isSlowConnection,
    // Calidad adaptativa
    quality: isSlowConnection ? 'auto:eco' : (isMobile ? 'auto:good' : 'auto:best'),
    // Máximo de imágenes paralelas
    parallelUploads: isSlowConnection ? 1 : (isMobile ? 2 : 3),
    // Formato preferido
    format: isSlowConnection ? 'auto' : 'auto'
  };
};

// ======================================================
// 🔧 FUNCIONES HELPER PARA URLs OPTIMIZADAS
// ======================================================

/**
 * Genera una URL optimizada de Cloudinary
 * @param {string} url - URL original
 * @param {string} preset - 'thumbnail' | 'card' | 'preview' | 'fullscreen' | 'original'
 * @returns {string} - URL optimizada
 */
export const getOptimizedCloudinaryUrl = (url, preset = 'preview') => {
  if (!url || typeof url !== 'string' || !url.includes('res.cloudinary.com')) return url;

  const transformation = CLOUDINARY_CONFIG.presets[preset] || CLOUDINARY_CONFIG.presets.preview;
  if (url.includes(`/upload/${transformation}/`)) return url;

  // Quitar transforms previos (segmentos con w_/h_/q_/f_/c_/… o comas)
  const cleaned = url.replace(
    /\/upload\/((?:[a-z]{1,3}_[^/]+,?)+)\//i,
    '/upload/'
  );

  return cleaned.replace('/upload/', `/upload/${transformation}/`);
};

/**
 * Genera URL con dimensiones específicas
 * @param {string} url - URL original
 * @param {number} width - Ancho deseado
 * @param {number} height - Alto deseado
 * @returns {string} - URL optimizada
 */
export const getSizedCloudinaryUrl = (url, width, height) => {
  if (!url || !url.includes('res.cloudinary.com')) return url;
  
  const { quality, format } = getDeviceConfig();
  const transformation = `w_${width},h_${height},c_fill,q_${quality},f_${format},dpr_auto`;
  return url.replace('/upload/', `/upload/${transformation}/`);
};

// ======================================================
// 🛡️ VALIDACIÓN EN DESARROLLO
// ======================================================

if (import.meta.env.DEV) {
  console.log('📋 ImageConfig cargado (NIVEL AIRBNB):');
  console.log(`  ☁️ Proveedor: ${IMAGE_PROVIDER.CURRENT}`);
  console.log(`  📁 Folder: ${CLOUDINARY_CONFIG.folder}`);
  console.log(`  🔧 Preset: ${CLOUDINARY_CONFIG.uploadPreset}`);
  console.log(`  ⚡ Subida paralela: ${CLOUDINARY_CONFIG.parallelUploads} imágenes`);
  console.log(`  💾 Cache: ${CACHE_CONFIG.databaseName} v${CACHE_CONFIG.version}`);
  
  const device = getDeviceConfig();
  console.log(`  📱 Dispositivo: ${device.isMobile ? 'Móvil' : 'Desktop'}`);
  console.log(`  🌐 Conexión: ${device.isSlowConnection ? 'Lenta' : 'Normal'}`);
  console.log(`  🎨 Calidad: ${device.quality}`);
}

// ======================================================
// 📤 EXPORTS
// ======================================================

export default {
  IMAGE_PROVIDER,
  CLOUDINARY_CONFIG,
  FIREBASE_STORAGE_CONFIG,
  CACHE_CONFIG,
  THUMBNAIL_CONFIG,
  getDeviceConfig,
  getOptimizedCloudinaryUrl,
  getSizedCloudinaryUrl
};